import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../redux/root-reducer';
import { campaignApplicantFavoriteUrl, campaignApplicantFavoritesUrl } from '../../../configurations/api/url';

/**
 * Optimistic favourite toggle for campaign applicants (per-campaign API).
 * Returns whether the API call succeeded.
 */
export function useApplicantFavorite(
    campaignId: string | undefined,
    influencerId: string | undefined,
    initiallyFavorited?: boolean,
) {
    const token = useSelector((s: RootState) => s.authentication?.token);
    const [favorited, setFavorited] = useState(Boolean(initiallyFavorited));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setFavorited(Boolean(initiallyFavorited));
    }, [campaignId, influencerId, initiallyFavorited]);

    const toggleFavorite = useCallback(
        async (e?: React.MouseEvent): Promise<boolean> => {
            e?.stopPropagation();
            e?.preventDefault();
            if (!token || !campaignId || !influencerId || saving) return false;
            const next = !favorited;
            setFavorited(next);
            setSaving(true);
            try {
                if (next) {
                    await axios.post(
                        campaignApplicantFavoritesUrl(campaignId),
                        { influencer_id: influencerId },
                        { headers: { Authorization: `Bearer ${token}` } },
                    );
                } else {
                    await axios.delete(campaignApplicantFavoriteUrl(campaignId, influencerId), {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                }
                return true;
            } catch {
                setFavorited(!next);
                return false;
            } finally {
                setSaving(false);
            }
        },
        [token, campaignId, influencerId, favorited, saving],
    );

    return { favorited, toggleFavorite, saving };
}
